/**
 * SmsSendModal.jsx — Ortak SMS gönderim modalı
 *
 * Props:
 *   open          {boolean}
 *   onOpenChange  {(bool) => void}
 *   userId        {string|undefined}   — varsa placeholder'lar backend'de doldurulur
 *   defaultPhone  {string|undefined}   — açılışta Telefonlar alanına yazılır
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  MessageSquare,
  X,
  Plus,
  ChevronDown,
  Info,
  Send,
  Loader2,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors';

export default function SmsSendModal({ open, onOpenChange, userId, defaultPhone }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);

  // Form state
  const [kaliplar, setKaliplar] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [kaliplarLoading, setKaliplarLoading] = useState(false);
  const [selectedKalipId, setSelectedKalipId] = useState('');
  const [telefonlar, setTelefonlar] = useState('');
  const [mesaj, setMesaj] = useState('');
  const [sending, setSending] = useState(false);

  const mesajRef = useRef(null);

  // Fetch kaliplar
  useEffect(() => {
    if (!open) return;
    setKaliplarLoading(true);
    axios
      .get(`${API}/admin/sms-kaliplari`, { headers: authHeaders() })
      .then((r) => {
        setKaliplar(r.data.kaliplar || []);
        setPlaceholders(r.data.desteklenen_placeholderlar || []);
      })
      .catch(() => {})
      .finally(() => setKaliplarLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // defaultPhone ile aç
  useEffect(() => {
    if (open) {
      setTelefonlar(defaultPhone || '');
      setSelectedKalipId('');
      setMesaj('');
    }
  }, [open, defaultPhone]);

  // Kalıp seçilince mesajı doldur
  const handleKalipChange = (e) => {
    const id = e.target.value;
    setSelectedKalipId(id);
    if (id) {
      const k = kaliplar.find((k) => k.kalip_id === id);
      if (k) setMesaj(k.mesaj);
    }
  };

  // Placeholder chip tıkla → cursor'a veya sona ekle
  const insertPlaceholder = (ph) => {
    const el = mesajRef.current;
    if (!el) {
      setMesaj((m) => m + ph);
      return;
    }
    const start = el.selectionStart ?? mesaj.length;
    const end = el.selectionEnd ?? mesaj.length;
    const next = mesaj.slice(0, start) + ph + mesaj.slice(end);
    setMesaj(next);
    // cursor'u placeholder'ın arkasına taşı
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + ph.length, start + ph.length);
    });
  };

  const handleSend = async () => {
    if (!telefonlar.trim() || !mesaj.trim()) {
      toast.error(tr('Telefon ve mesaj zorunludur.', 'Phone and message are required.'));
      return;
    }
    setSending(true);
    try {
      const body = {
        telefonlar: telefonlar.trim(),
        mesaj: mesaj.trim(),
      };
      if (selectedKalipId) body.kalip_id = selectedKalipId;
      if (userId) body.user_id = userId;

      const r = await axios.post(`${API}/admin/sms/send`, body, { headers: authHeaders() });
      const { sent = 0, failed = [], demo_mode } = r.data || {};

      if (demo_mode) {
        toast.success(tr('Demo mod — SMS gerçekte gönderilmedi.', 'Demo mode — SMS was not actually sent.'));
      } else {
        let msg = tr(`${sent} SMS gönderildi.`, `${sent} SMS sent.`);
        if (failed.length > 0) {
          msg += ' ' + tr(
            `${failed.length} başarısız: ${failed.map((f) => f.telefon).join(', ')}`,
            `${failed.length} failed: ${failed.map((f) => f.telefon).join(', ')}`
          );
          toast.error(msg);
        } else {
          toast.success(msg);
        }
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="sms-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => !sending && onOpenChange(false)}
      >
        <motion.div
          key="sms-modal-box"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl border-2 border-[var(--border-light)] shadow-2xl w-full max-w-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[var(--border-light)]">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-emerald-600" />
              <h2 className="font-display text-base font-bold text-[var(--text-main)]">
                {tr('SMS Gönder', 'Send SMS')}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => !sending && onOpenChange(false)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-[var(--text-muted)]"
              disabled={sending}
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {/* Üst satır 3 alan */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Başlık / Sağlayıcı */}
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide">
                  {tr('Başlık (Sağlayıcı)', 'Title (Provider)')}
                </label>
                <div className="relative">
                  <select
                    disabled
                    className={INPUT_CLS + ' opacity-60 cursor-not-allowed appearance-none pr-8'}
                    value="NETGSM"
                    onChange={() => {}}
                  >
                    <option value="NETGSM">NETGSM</option>
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
                  />
                </div>
              </div>

              {/* SMS Kalıbı */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                    {tr('SMS Kalıbı', 'SMS Template')}
                  </label>
                  <a
                    href="/admin/definitions?tab=sms"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-0.5 text-[10px] font-bold text-[var(--brand-primary)] hover:underline"
                  >
                    <Plus size={11} />
                    {tr('Yeni Ekle', 'Add New')}
                  </a>
                </div>
                <div className="relative">
                  <select
                    className={INPUT_CLS + ' appearance-none pr-8' + (kaliplarLoading ? ' opacity-60' : '')}
                    value={selectedKalipId}
                    onChange={handleKalipChange}
                    disabled={kaliplarLoading}
                  >
                    <option value="">{tr('— Kalıp seç —', '— Select template —')}</option>
                    {kaliplar
                      .filter((k) => k.aktif !== false)
                      .map((k) => (
                        <option key={k.kalip_id} value={k.kalip_id}>
                          {k.ad}
                        </option>
                      ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
                  />
                </div>
              </div>

              {/* Telefonlar */}
              <div>
                <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide">
                  {tr('Telefonlar', 'Phone Numbers')}
                </label>
                <input
                  type="text"
                  value={telefonlar}
                  onChange={(e) => setTelefonlar(e.target.value)}
                  placeholder={tr('5321111111 veya tire ile çoklu', '5321111111 or hyphen-separated')}
                  className={INPUT_CLS}
                  data-testid="sms-modal-phones"
                />
              </div>
            </div>

            {/* Mesaj */}
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide">
                {tr('Mesaj', 'Message')}
              </label>
              <textarea
                ref={mesajRef}
                rows={5}
                value={mesaj}
                onChange={(e) => setMesaj(e.target.value)}
                className={INPUT_CLS + ' resize-none'}
                placeholder={tr('Mesajınızı girin veya kalıp seçin...', 'Enter your message or select a template...')}
                data-testid="sms-modal-message"
              />

              {/* Placeholder bilgi notu + chip'ler — sadece userId varsa */}
              {userId && (
                <div className="mt-2">
                  <p className="text-[10px] text-[var(--text-muted)] font-semibold mb-1.5 flex items-center gap-1">
                    <Info size={11} />
                    {tr(
                      'Öğrenci bağlamı aktif — {xxx} placeholder\'ları backend\'de otomatik doldurulur.',
                      'Student context active — {xxx} placeholders will be auto-filled by the backend.'
                    )}
                  </p>
                  {placeholders.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {placeholders.map((ph) => (
                        <button
                          key={ph}
                          type="button"
                          onClick={() => insertPlaceholder(ph)}
                          className="px-2 py-0.5 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-700 text-[11px] font-mono font-semibold hover:bg-cyan-100 transition-colors"
                        >
                          {ph}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bilgi bandı */}
            <div className="flex items-start gap-2 px-3.5 py-2.5 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] font-semibold text-blue-700 leading-relaxed">
                {tr(
                  'DİKKAT: Gönderme işleminden sonra bir süre bekleyiniz. Çoklu göndermek için telefonlar arasına tire (-) işareti koyunuz.',
                  'NOTE: Please wait a moment after sending. For multiple recipients, separate phone numbers with a hyphen (-).'
                )}
              </p>
            </div>
          </div>

          {/* Footer butonlar */}
          <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t-2 border-[var(--border-light)] bg-gray-50/60">
            <motion.button
              type="button"
              onClick={() => !sending && onOpenChange(false)}
              disabled={sending}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-4 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold text-[var(--text-main)] hover:bg-gray-100 disabled:opacity-40 bg-white"
            >
              {tr('Kapat', 'Close')}
            </motion.button>
            <motion.button
              type="button"
              onClick={handleSend}
              disabled={sending || !telefonlar.trim() || !mesaj.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-500 text-white text-sm font-bold border-b-4 border-emerald-700 hover:bg-emerald-600 disabled:opacity-40"
              data-testid="sms-modal-send-btn"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? tr('Gönderiliyor...', 'Sending...') : tr('SMS Gönder', 'Send SMS')}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
